import { useEffect, useState } from "react";
import { LuArrowLeft } from "react-icons/lu";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import apiClient from "../../../api-services/ApiClient";
import Preloader from "../../../app-components/preloader/Preloader";
import { SITELINKS } from "../../../app-routes/links";
import { OptionProps } from "../../../interface-and-types";
import {
  ActionTrackerInterface,
  ActionTrackerSchema,
} from "../../../interface-and-types/inspection.schema";

const CreateActionTracker = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [vessels, setVessels] = useState<OptionProps[]>([]);
  const [form, setForm] = useState<Partial<ActionTrackerInterface>>({});
  const [criticalEquipmentList, setCriticalEquipmentList] = useState<any[]>([]);
  const [defectType, setDefectType] = useState<any[]>([]);
  const [marineSuperintendent, setMarineSuperintendent] = useState<any[]>([]);
  const [priority, setPriority] = useState<any[]>([]);
  const [serverError, setServerError] = useState<string>("");
  const [errors, setErrors] = useState<
    Partial<Record<keyof ActionTrackerInterface, string>>
  >({});

  useEffect(() => {
    getVessels();
    getCriticalEquipmentTypes();
    getDefectType();
    getMarineSuperintendent();
    getPriorities();
  }, []);

  const handleInput = (e: any) => {
    setForm((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const getVessels = async () => {
    try {
      // const res: any = await apiClient.get("UserVessels/Assignments");
      const res: any = await apiClient.get(
        "Vessels?PageSize=1000&view=withuser"
      );
      if (res && res.data) {
        const options: OptionProps[] = res.data.data.map(
          (i: any, index: number) => ({
            label: i.name,
            value: i.id,
            name: "Vessels",
            key: index,
          })
        );
        setVessels(options);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const getCriticalEquipmentTypes = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/CriticalEquipmentTypes");
      setCriticalEquipmentList(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDefectType = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("DefectType");
      setDefectType(res.data.data.filter((item:any) => !item.isDeleted));
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMarineSuperintendent = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get(
        "users?Rolename=MarineSuperintendent&pageSize=1000"
      );
      setMarineSuperintendent(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorities = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/Priorities");
      setPriority(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const formData = { ...form };

    if (files) {
      const filesArray = Array.from(files);

      const base64Promises = filesArray.map(async (file) => {
        const base64 = await fileToBase64(file);
        return {
          base64Image: base64,
          mimeType: file.type,
        };
      });

      // Convert all files to Base64
      Promise.all(base64Promises).then((base64Arr: any) => {
        formData.defectCheckListAttachments = base64Arr;
        setForm(formData);
      });
    }
  };

  const handleCreate = async (e: any) => {
    e.preventDefault();
    setErrors({});
    setServerError("");

    const result = ActionTrackerSchema.safeParse(form);

    if (result.success) {
      setLoading(true);
      try {
        const data = result.data;
        await apiClient.post("ActionTracker/MarineSuperintendent/Create", data);
        toast.success("successful!", {
          autoClose: 60000,
          theme: "colored", // Options: "light", "dark", "colored"
        });

      } catch (error: any) {
        setServerError(JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    } else {
      const formattedErrors: Partial<
        Record<keyof ActionTrackerInterface, string>
      > = {};

      result.error.errors.forEach((err: any) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof ActionTrackerInterface;
          formattedErrors[key] = err.message;
        }
      });

      setErrors(formattedErrors);
      setServerError(
        "Form validation error occured! Kindly complete all required fields."
      );
    }
  };

  return (
    <main>
      {/* page lead */}
      <section className="flex justify-between items-center">
        <h4 className="font-normal lg:text-xl text-slate-800 flex gap-3">
          <Link to={SITELINKS.TRACKER} className="text-red-600 text-3xl">
            <LuArrowLeft />
          </Link>
          <span>Add New Action Tracker</span>
        </h4>
      </section>

      {/* page content */}
      <section className="pt-2 mx-auto">
        <div className="rounded-lg pb-5 border border-gray-200 flex gap-6">
          <div className="w-full p-6 rounded-lg shadow bg-white">
            <form className="w-full" onSubmit={handleCreate}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 w-full">
                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Vessel Name
                  </label>
                  <select
                    name="vesselId"
                    value={form?.vesselId}
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                    onChange={handleInput}
                  >
                    <option value={""}>Select</option>
                    {vessels.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.vesselId}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Equpiment Name
                  </label>
                  <input
                    name="equipmentName"
                    value={form.equipmentName}
                    type="text"
                    placeholder=""
                    onChange={handleInput}
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  />
                  <span className="text-red-600 text-[10px]">
                    {errors.equipmentName}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Critical Equipment
                  </label>
                  <select
                    name="criticalEquipment"
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                    value={form?.criticalEquipment}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    {criticalEquipmentList.map((item, i) => {
                      return (
                        <option key={i} value={item?.value}>
                          {item?.name}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.criticalEquipment}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Type Of Defect
                  </label>
                  <select
                    name="defectTypeId"
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                    value={form?.defectTypeId}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    {defectType.map((item, i) => {
                      return (
                        <option key={i} value={item?.id}>
                          {item?.name}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.defectTypeId}
                  </span>
                </div>

                <div className="">
                  <label className="text-gray-500 text-xs font-medium">
                    Select Marine Superintendent
                  </label>
                  <select
                    name="marineSuperintendentId"
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                    value={form?.marineSuperintendentId}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    {marineSuperintendent.map((item, i) => {
                      return (
                        <option key={i} value={item?.id}>
                          {item?.firstName} {item?.lastName}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.marineSuperintendentId}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Priority
                  </label>
                  <select
                    name="priority"
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                    value={form?.priority}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    {priority.map((item, i) => {
                      return (
                        <option key={i} value={item?.value}>
                          {item?.name}  ({item?.value})
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.priority}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Responsible Party
                  </label>
                  <input
                    name="responsibleParty"
                    value={form?.responsibleParty}
                    type="text"
                    placeholder=""
                    onChange={handleInput}
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  />
                  <span className="text-red-600 text-[10px]">
                    {errors.responsibleParty}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Resources Needed
                  </label>
                  <select
                    name="resourcesNeeded"
                    className="disabled:bg-gray-100 w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                    value={form?.resourcesNeeded}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    <option value="Human">Human</option>
                    <option value="Material">Material</option>
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.resourcesNeeded}
                  </span>
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <label className="text-gray-500 text-xs font-medium">
                    Write a comment
                  </label>
                  <textarea
                    name="comments"
                    value={form?.comments}
                    placeholder=""
                    onChange={handleInput}
                    className="h-[150px] w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  ></textarea>
                  <span className="text-red-600 text-[10px]">
                    {errors.comments}
                  </span>
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <label className="text-gray-500 text-xs font-medium">
                    Description of Defect
                  </label>
                  <textarea
                    name="descriptionOfDefect"
                    value={form?.descriptionOfDefect}
                    placeholder=""
                    onChange={handleInput}
                    className="h-[150px] w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  ></textarea>
                  <span className="text-red-600 text-[10px]">
                    {errors.descriptionOfDefect}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Target Close-out Date
                  </label>
                  <input
                    name="targetCloseOutDate"
                    value={form?.targetCloseOutDate}
                    type="text"
                    placeholder=""
                    onFocus={(e) => (e.target.type = "date")}
                    onBlur={(e) => (e.target.type = "text")}
                    onChange={handleInput}
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  />
                  <span className="text-red-600 text-[10px]">
                    {errors.targetCloseOutDate}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Close-out Date
                  </label>
                  <input
                    name="dateClosed"
                    value={form?.dateClosed}
                    type="text"
                    placeholder=""
                    onFocus={(e) => (e.target.type = "date")}
                    onBlur={(e) => (e.target.type = "text")}
                    onChange={handleInput}
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  />
                  <span className="text-red-600 text-[10px]">
                    {errors.dateClosed}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Percentage Of Action Close-Out Rate
                  </label>
                  <input
                    name="percentageOfActionClosedOut"
                    value={form?.percentageOfActionClosedOut}
                    type="text"
                    placeholder=""
                    onChange={handleInput}
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  />
                  <span className="text-red-600 text-[10px]">
                    {errors.percentageOfActionClosedOut}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-xs font-medium">
                    Attach Pictures
                  </label>
                  <input
                    multiple
                    type="file"
                    id="file-upload"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e)}
                    className="w-full p-3 border rounded focus:outline-none focus:ring focus:ring-blue-500"
                  />
                  <span className="text-red-600 text-[10px]">
                    {errors.percentageOfActionClosedOut}
                  </span>
                </div>
              </div>

              <div className="mt-4">
                {form.defectCheckListAttachments && (
                  <>
                    <p className="flex-col text-xs text-gray-500 mb-2 font-medium">
                      Attached Images Preview
                    </p>
                    <div className="flex flex-wrap gap-4 sm:grid-cols-2 w-full">
                      {form.defectCheckListAttachments.map((img, index) => (
                        <div className="relative">
                          {/* <span className="bg-purple-800 text-white rounded-full border absolute top-1 right-1 text-xs p-1 px-2 font-medium">
                            {index + 1}
                          </span> */}
                          <img
                            src={img.base64Image}
                            className="h-[100px] rounded-lg border bg-gray-100"
                            alt="base64"
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="text-red-600 text-xs mt-4">{serverError}</div>

              <div className="mt-2">
                <button
                  type="submit"
                  className="flex gap-2 items-center bg-red-700 shadow p-3 rounded-lg text-white font-medium px-4 w-1/4 justify-center"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <ToastContainer position="bottom-left" hideProgressBar />
      <Preloader loading={loading} />
    </main>
  );
};

export default CreateActionTracker;
